/**
 * API news-документов проекта: список (для picker'а) + пакетный резолв (для children_grid).
 */

import { getBffEmployeeHeaders } from '@/lib/current-user-mock';
import { throwBffError } from './projects';

export interface NewsSummaryDto {
  documentId: string;
  title: string | null;
  newsCoverUrl: string | null;
  newsPublishedAt: string | null;
  createdBy: string | null;
  authorName: string | null;
  authorAvatarUrl: string | null;
}

async function parseJsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) await throwBffError(res);
  return res.json() as Promise<T>;
}

/** Список всех news-документов проекта. Сортировка: published_at DESC, потом updated_at DESC. */
export async function listProjectNews(projectId: string): Promise<NewsSummaryDto[]> {
  const res = await fetch(`/api/bff/projects/${encodeURIComponent(projectId)}/news`, {
    headers: { Accept: 'application/json', ...getBffEmployeeHeaders() },
    credentials: 'include',
  });
  return parseJsonOrThrow<NewsSummaryDto[]>(res);
}

/**
 * Пакетный резолв `document_id → NewsSummaryDto`. Возвращаются только документы
 * с `content_kind = 'news'` и `project_id` совпадающим. Лимит 200 id на запрос.
 */
export async function resolveProjectNewsByIds(
  projectId: string,
  ids: string[],
): Promise<NewsSummaryDto[]> {
  if (ids.length === 0) return [];
  const res = await fetch(`/api/bff/projects/${encodeURIComponent(projectId)}/news/resolve`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...getBffEmployeeHeaders(),
    },
    body: JSON.stringify({ ids }),
  });
  return parseJsonOrThrow<NewsSummaryDto[]>(res);
}

// =========================================================================
// Главная лента новостей (кросс-проектная)
// =========================================================================

/** Один элемент кросс-проектной ленты. Контракт совпадает с NewsFeedItemDto в bff. */
export interface NewsFeedItem {
  documentId: string;
  projectId: string;
  /**
   * Human-readable код проекта (issue_key_prefix, например, "PC02").
   * Используется для построения URL вида `/project/PC02/news/<docId>`.
   * Null fallback'ится на projectId.
   */
  projectKey: string | null;
  projectName: string;
  projectIcon: string | null;
  /** Phase 8: счётчики статов в карточке ленты. */
  viewCount: number;
  commentCount: number;
  /** Лайки 👍 (Phase 7). Агрегат из document.news_reaction WHERE kind='like'. */
  likeCount: number;
  /** Дизлайки 👎 (Phase 7). Агрегат из document.news_reaction WHERE kind='dislike'. */
  dislikeCount: number;
  /** Phase 9: персональные коллекции текущего пользователя. */
  isBookmarked: boolean;
  isReadLater: boolean;
  title: string;
  excerpt: string | null;
  coverUrl: string | null;
  publishedAt: string; // ISO-8601
  isPinned: boolean;
  emoji: string | null;
  author: {
    employeeId: string | null;
    name: string | null;
    avatarUrl: string | null;
  };
  categories: Array<{ id: string; name: string; color: string }>;
}

export interface NewsFeedPage {
  items: NewsFeedItem[];
  nextCursor: string | null;
}

export interface NewsFeedQuery {
  /**
   * spaceId для фильтрации. Опционален: если не задан — кросс-space лента
   * (видимы проекты из всех space'ов, в которых состоит пользователь).
   */
  spaceId?: string;
  /** Cursor предыдущей страницы (null для первой). */
  cursor?: string | null;
  /** Размер страницы (1..50, дефолт 20). */
  limit?: number;
  /** Фильтр по одному проекту (для страницы /project/:uuid с предустановленным фильтром). */
  projectId?: string;
  /** Фильтр по рубрикам (OR-семантика). UUIDs. */
  categoryIds?: string[];
  /** Сортировка. `pinned` — только закреплённые. `popular` — по статам (Phase 8). */
  sort?: 'newest' | 'pinned' | 'popular';
  /** Phase 9: фильтр по списку documentId (для страницы /saved). */
  documentIds?: string[];
}

/**
 * Запрос одной страницы ленты новостей.
 *
 * Backend: `GET /api/bff/news/feed?spaceId=…&cursor=…&limit=…&sort=…&categoryIds=…&projectId=…`
 *
 * См. backend/services/prona-marketplace-bff/src/bff/news-feed.service.ts.
 */
export async function fetchNewsFeed(query: NewsFeedQuery): Promise<NewsFeedPage> {
  const params = new URLSearchParams();
  if (query.spaceId) params.set('spaceId', query.spaceId);
  if (query.cursor) params.set('cursor', query.cursor);
  if (query.limit != null) params.set('limit', String(query.limit));
  if (query.projectId) params.set('projectId', query.projectId);
  if (query.categoryIds && query.categoryIds.length > 0) {
    params.set('categoryIds', query.categoryIds.join(','));
  }
  if (query.sort) params.set('sort', query.sort);
  if (query.documentIds && query.documentIds.length > 0) {
    params.set('documentIds', query.documentIds.join(','));
  }

  const res = await fetch(`/api/bff/news/feed?${params.toString()}`, {
    headers: { Accept: 'application/json', ...getBffEmployeeHeaders() },
    credentials: 'include',
  });
  return parseJsonOrThrow<NewsFeedPage>(res);
}

// =========================================================================
// Полный snapshot для детального экрана (Phase 3)
// =========================================================================

/** Один элемент категории, привязанной к news-документу. */
export interface NewsCategoryDto {
  id: string;
  project_id: string;
  name: string;
  color: string;
  sort_order: number;
  created_at: string;
}

/** Полный snapshot news-документа для детального экрана ленты. */
export interface NewsArticleSnapshotDto {
  meta: {
    documentId: string;
    projectId: string;
    title: string;
    description: string | null;
    icon: string | null;
    newsCoverUrl: string | null;
    newsPublishedAt: string | null;
    excerpt: string | null;
    isPinned: boolean;
    createdBy: string | null;
    updatedAt: string;
    contentKind: string;
    /** Phase 8: просмотры. */
    viewCount: number;
    /** Лайки 👍 — baseline для fallback в детальном экране (P11). */
    likeCount: number;
    /** Дизлайки 👎 — baseline для fallback в детальном экране (P11). */
    dislikeCount: number;
    /**
     * Может ли текущий пользователь модерировать комментарии: удалять чужие
     * сообщения. True для автора публикации и project-админов
     * (право `project:members:manage`).
     */
    viewerCanModerate: boolean;
  };
  /**
   * Блоки контента (DocumentBlockData[] от DSS). Тип нестрогий —
   * news-блоки имеют динамические поля (newsParagraphText, etc.),
   * полностью описаны в src/app/project/[uuid]/initiation/board/board-yjs.ts.
   */
  blocks: Array<{
    id: string;
    type: string;
    content: string;
    [key: string]: unknown;
  }>;
  /**
   * HTML-снапшот тела единого PM-документа (board-document-unified). Приходит
   * для мигрированных документов (`bodyModelVersion >= 2`) — у них `blocks`
   * стейл, и читалка рендерит bodyHtml (Ф0 kb-unified-document-restoration).
   */
  bodyHtml?: string;
  categories: NewsCategoryDto[];
  author: {
    employeeId: string | null;
    name: string | null;
    avatarUrl: string | null;
  };
  project: {
    id: string;
    /** Human-readable код (issue_key_prefix), для URL `/project/PC02/`. */
    key: string | null;
    name: string;
    icon: string | null;
  };
}

/**
 * Загрузить полный snapshot news-документа для детального экрана.
 * Один HTTP-запрос вместо WS-подключения к Hocuspocus.
 *
 * Backend: GET /api/bff/projects/:projectId/documents/:documentId/news-snapshot
 */
// =========================================================================
// Поисковые подсказки (Phase 4)
// =========================================================================

export interface NewsSearchSuggestNewsItem {
  documentId: string;
  projectId: string;
  projectKey: string | null;
  projectName: string;
  title: string;
  excerpt: string | null;
  publishedAt: string;
  rank: number;
}

export interface NewsSearchSuggestAuthor {
  employeeId: string;
  name: string;
  avatarUrl: string | null;
  newsCount: number;
}

export interface NewsSearchSuggestCategory {
  id: string;
  name: string;
  color: string;
  projectId: string;
  projectKey: string | null;
  projectName: string;
}

export interface NewsSearchResultDto {
  news: NewsSearchSuggestNewsItem[];
  authors: NewsSearchSuggestAuthor[];
  categories: NewsSearchSuggestCategory[];
}

/**
 * Поисковые подсказки. Использует полнотекстовый tsvector-индекс.
 * Backend: GET /api/bff/news/feed/search?q=...&spaceId=...
 */
export async function fetchNewsSearchSuggestions(
  q: string,
  spaceId?: string,
): Promise<NewsSearchResultDto> {
  const params = new URLSearchParams();
  params.set('q', q);
  if (spaceId) params.set('spaceId', spaceId);
  const res = await fetch(`/api/bff/news/feed/search?${params.toString()}`, {
    headers: { Accept: 'application/json', ...getBffEmployeeHeaders() },
    credentials: 'include',
  });
  return parseJsonOrThrow<NewsSearchResultDto>(res);
}

export async function fetchNewsSnapshot(
  projectId: string,
  documentId: string,
): Promise<NewsArticleSnapshotDto> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(
      documentId,
    )}/news-snapshot`,
    {
      headers: { Accept: 'application/json', ...getBffEmployeeHeaders() },
      credentials: 'include',
    },
  );
  return parseJsonOrThrow<NewsArticleSnapshotDto>(res);
}

// =========================================================================
// Комментарии под news-публикацией (Phase 6)
// =========================================================================

export interface NewsCommentsThreadDto {
  channelId: string | null;
  topicId: string | null;
  messageCount: number;
}

/**
 * Получить thread комментариев под news. Возвращает channelId=null если ещё
 * никто не комментировал — frontend в этом случае показывает CTA «Оставить
 * первый комментарий» → вызывает ensureNewsCommentsThread.
 */
export async function fetchNewsCommentsThread(
  projectId: string,
  documentId: string,
): Promise<NewsCommentsThreadDto> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(
      documentId,
    )}/comments/thread`,
    {
      headers: { Accept: 'application/json', ...getBffEmployeeHeaders() },
      credentials: 'include',
    },
  );
  return parseJsonOrThrow<NewsCommentsThreadDto>(res);
}

/**
 * Lazy-create thread комментариев. Идемпотентен — если уже создан, возвращает
 * существующий. Вызывается перед отправкой первого комментария.
 */
export async function ensureNewsCommentsThread(
  projectId: string,
  documentId: string,
): Promise<{ channelId: string; topicId: string }> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(
      documentId,
    )}/comments/ensure`,
    {
      method: 'POST',
      headers: { Accept: 'application/json', ...getBffEmployeeHeaders() },
      credentials: 'include',
    },
  );
  return parseJsonOrThrow<{ channelId: string; topicId: string }>(res);
}

/**
 * Удалить комментарий под news-публикацией. Backend разрешает удаление:
 *   - автору сообщения (всегда);
 *   - автору публикации (`document_meta.created_by`);
 *   - project-/space-админу (`project:members:manage` / `space:members:manage`).
 *
 * Идёт через BFF (а не chatApi.deleteMessage), потому что chat-service
 * пропускает только author + channel-owner/admin, а в news_comments-канале
 * все добавляются как 'member'.
 */
export async function deleteNewsComment(
  projectId: string,
  documentId: string,
  messageId: string,
): Promise<void> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(
      documentId,
    )}/comments/${encodeURIComponent(messageId)}`,
    {
      method: 'DELETE',
      headers: { Accept: 'application/json', ...getBffEmployeeHeaders() },
      credentials: 'include',
    },
  );
  await parseJsonOrThrow<{ ok: boolean }>(res);
}

// =========================================================================
// Реакции 👍/👎 (Phase 7)
// =========================================================================

export type NewsReactionKind = 'like' | 'dislike';

export interface NewsReactionAggregateDto {
  likes: number;
  dislikes: number;
  myReaction: NewsReactionKind | null;
  recentVoters: Array<{
    employeeId: string;
    name: string | null;
    avatarUrl: string | null;
    kind: NewsReactionKind;
  }>;
}

export async function fetchNewsReactions(
  projectId: string,
  documentId: string,
): Promise<NewsReactionAggregateDto> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(
      documentId,
    )}/reactions`,
    {
      headers: { Accept: 'application/json', ...getBffEmployeeHeaders() },
      credentials: 'include',
    },
  );
  return parseJsonOrThrow<NewsReactionAggregateDto>(res);
}

export async function setNewsReaction(
  projectId: string,
  documentId: string,
  kind: NewsReactionKind,
): Promise<NewsReactionAggregateDto> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(
      documentId,
    )}/reactions`,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...getBffEmployeeHeaders(),
      },
      credentials: 'include',
      body: JSON.stringify({ kind }),
    },
  );
  return parseJsonOrThrow<NewsReactionAggregateDto>(res);
}

// =========================================================================
// Закладки / Читать позже (Phase 9)
// =========================================================================

export type NewsCollectionKind = 'bookmark' | 'read_later';

const collectionPath: Record<NewsCollectionKind, string> = {
  bookmark: 'bookmarks',
  read_later: 'read-later',
};

export async function listNewsCollection(kind: NewsCollectionKind): Promise<string[]> {
  const res = await fetch(`/api/bff/me/news/${collectionPath[kind]}`, {
    headers: { Accept: 'application/json', ...getBffEmployeeHeaders() },
    credentials: 'include',
  });
  const data = await parseJsonOrThrow<{ items: string[] }>(res);
  return data.items ?? [];
}

export async function addToNewsCollection(
  kind: NewsCollectionKind,
  documentId: string,
): Promise<void> {
  const res = await fetch(`/api/bff/me/news/${collectionPath[kind]}`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...getBffEmployeeHeaders(),
    },
    credentials: 'include',
    body: JSON.stringify({ documentId }),
  });
  await parseJsonOrThrow<{ ok: boolean }>(res);
}

export async function removeFromNewsCollection(
  kind: NewsCollectionKind,
  documentId: string,
): Promise<void> {
  const res = await fetch(
    `/api/bff/me/news/${collectionPath[kind]}/${encodeURIComponent(documentId)}`,
    {
      method: 'DELETE',
      headers: { Accept: 'application/json', ...getBffEmployeeHeaders() },
      credentials: 'include',
    },
  );
  await parseJsonOrThrow<{ ok: boolean }>(res);
}

// =========================================================================
// Просмотры (Phase 8)
// =========================================================================

/**
 * Зарегистрировать просмотр news. Backend применяет rate-limit (1 инкремент в час).
 * Вызывается из NewsArticleView при mount детального экрана.
 *
 * Без явной обработки ошибок — failure-tolerant, view-tracking не критичен.
 */
export async function registerNewsView(
  projectId: string,
  documentId: string,
): Promise<{ viewCount: number; counted: boolean }> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(
      documentId,
    )}/news/view`,
    {
      method: 'POST',
      headers: { Accept: 'application/json', ...getBffEmployeeHeaders() },
      credentials: 'include',
    },
  );
  return parseJsonOrThrow<{ viewCount: number; counted: boolean }>(res);
}

// =========================================================================
// Stats (P11) — лёгкий батч-endpoint для счётчиков
// =========================================================================

export interface NewsStatsItem {
  documentId: string;
  viewCount: number;
  commentCount: number;
  likeCount: number;
  dislikeCount: number;
}

export interface NewsStatsResponse {
  items: NewsStatsItem[];
}

/**
 * Батч-загрузка свежих счётчиков для набора news-документов.
 *
 * Backend: `GET /api/bff/news/feed/stats?docIds=<csv>` без Redis-кэша.
 * Используется хуком `useNewsStats` (staleTime=5s) — рендер карточки/детального
 * экрана читает viewCount/commentCount/likeCount из live map, fallback'ится
 * на baseline из feed-DTO / snapshot-DTO.
 *
 * Это позволяет:
 *  - кэшировать тяжёлые данные (snapshot, feed) на 5 минут;
 *  - не инвалидировать эти кэши при просмотре/лайке/комменте;
 *  - получать «свежие» счётчики точечно, не вытягивая весь feed заново.
 */
export async function fetchNewsStats(docIds: string[]): Promise<NewsStatsResponse> {
  if (docIds.length === 0) return { items: [] };
  // Дедупликация + ограничение в 100 — соответствует серверному лимиту.
  const ids = Array.from(new Set(docIds)).slice(0, 100);
  const params = new URLSearchParams({ docIds: ids.join(',') });
  const res = await fetch(`/api/bff/news/feed/stats?${params.toString()}`, {
    headers: { Accept: 'application/json', ...getBffEmployeeHeaders() },
    credentials: 'include',
  });
  return parseJsonOrThrow<NewsStatsResponse>(res);
}

export async function removeNewsReaction(
  projectId: string,
  documentId: string,
): Promise<NewsReactionAggregateDto> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(
      documentId,
    )}/reactions`,
    {
      method: 'DELETE',
      headers: { Accept: 'application/json', ...getBffEmployeeHeaders() },
      credentials: 'include',
    },
  );
  return parseJsonOrThrow<NewsReactionAggregateDto>(res);
}
